import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import Typography from 'antd/es/typography';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { useCreateAgentExecution } from '../../queries/agent-executions';
import { JsonSchemaForm } from '../JsonSchemaForm';

import { ConfigPreview, SchemaPreview, Section, SmallSecondaryText } from './RunAgent.styles';
import { createExecutionPayload, formatInputSchema, getExecutionConfigSummary, type RunAgentConfig, type RunAgentFormData } from './RunAgent.utils';

const { Title, Text } = Typography;

interface RunAgentComponentProps {
  config: RunAgentConfig;
  onClose: () => void;
  onExecutionCreated?: (executionId: string) => void;
}

export const RunAgentComponent: React.FC<RunAgentComponentProps> = ({
  config,
  onClose,
  onExecutionCreated,
}) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm<RunAgentFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const createExecutionMutation = useCreateAgentExecution();

  const { agentId, configurationVersion, teamId, shouldNavigateToExecutions = true } = config;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Create the execution
      const executionPayload = createExecutionPayload(values, configurationVersion);
      const execution = await createExecutionMutation.mutateAsync(executionPayload);

      void message.success('Agent execution started successfully');
      onClose();
      form.resetFields();

      // Call the callback if provided
      if (onExecutionCreated) {
        onExecutionCreated(execution.agentExecutionId);
      }

      // Navigate to execution detail page if teamId is provided and navigation is enabled
      if (teamId && shouldNavigateToExecutions) {
        void navigate(
          `/team/${teamId}/agents/${agentId}/executions/${execution.agentExecutionId}`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.error?.message ||
            'Failed to start agent execution'
          : 'Failed to start agent execution';
      void message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const executionSummary = getExecutionConfigSummary(configurationVersion);
  const inputSchemaDisplay = formatInputSchema(configurationVersion.inputSchema);

  return (
    <div>
      <Section>
        <Title level={5} style={{ marginBottom: `${theme.marginXS}px` }}>
          Input Schema
        </Title>
        <Text
          type="secondary"
          style={{ display: 'block', marginBottom: `${theme.marginMD}px` }}
        >
          Review the expected input structure for this agent.
        </Text>

        <SchemaPreview>
          {Object.keys(configurationVersion.inputSchema).length > 0 ? (
            inputSchemaDisplay
          ) : (
            <Text type="secondary">No input schema defined</Text>
          )}
        </SchemaPreview>
      </Section>

      <Section>
        <Title level={5} style={{ marginBottom: `${theme.marginXS}px` }}>
          Agent Input
        </Title>
        <Text
          type="secondary"
          style={{ display: 'block', marginBottom: `${theme.marginMD}px` }}
        >
          Provide the input data for this agent execution based on the defined
          input schema.
        </Text>

        <Form form={form} layout="vertical">
          <JsonSchemaForm
            schema={configurationVersion.inputSchema}
            form={form}
            name="agentInput"
          />
        </Form>
      </Section>

      <Section>
        <Title level={5} style={{ marginBottom: `${theme.marginXS}px` }}>
          Execution Configuration
        </Title>
        <ConfigPreview>
          <div>
            Max Actions: {executionSummary.maxActions}
          </div>
          <div>
            Planning Interval: {executionSummary.planningInterval}
          </div>
          <div>
            Timeout: {executionSummary.timeoutMinutes} minutes
          </div>
          <div>
            Tools: {executionSummary.toolsCount} configured
          </div>
        </ConfigPreview>
      </Section>

      <Section>
        <SmallSecondaryText>
          This will create a new execution using the current configuration
          (Version {configurationVersion.versionNumber}). The agent will run
          asynchronously and you can monitor its progress in the executions
          tab.
        </SmallSecondaryText>
      </Section>

      <div style={{ display: 'flex', gap: theme.marginXS, justifyContent: 'flex-end', marginTop: theme.marginLG }}>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          Run Agent
        </Button>
      </div>
    </div>
  );
}; 